import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Island, useSubscription } from '@rta/rta-blueprint-library';

import { ControllerDisplayModeSupport } from '@features/navigation/types';
import { BackPanelIcon, BackPanelsIcon, FrontPanelIcon, RtaFeatureIconButton, SidePanelsIcon } from '@components';
import { useDesign } from '@hooks';
import { Box, Divider, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import { SquareX } from 'lucide-react';
import { Feature, showPanelFeature, useDecreaseFontSizeFeature, useDisplayBlueprintViewModeFeature, useDisplayManufacturingModeFeature, useDisplayOverheadModeFeature, useDisplaySkuOverheadModeFeature, useIncreaseFontSizeFeature, useShowAllDimensions, useShowBacksplashFeature, useShowCornerTrimFeature } from '@features';
import { ControllerFontSizeSupport } from '@features/dimensions/types';

type PanelIconComponent = typeof FrontPanelIcon;

export const ActionMenuBar = () => {
	const design = useDesign();
	const controller = design[0] as (typeof design[0]) & ControllerDisplayModeSupport & ControllerFontSizeSupport;

	useSubscription(controller, true, [['designer-options-adjust-font-size', 'updated'], ['navigation-panel', 'updated']]);

	const showAllDimensions = useShowAllDimensions();
	const increaseFontSize = useIncreaseFontSizeFeature();
	const decreaseFontSize = useDecreaseFontSizeFeature();
	const blueprintViewFeature = useDisplayBlueprintViewModeFeature();
	const displaySkuOverheadModeFeature = useDisplaySkuOverheadModeFeature();
	const displayOverheadModeFeature = useDisplayOverheadModeFeature();
	const manufacturingModeFeature = useDisplayManufacturingModeFeature();
	const showBacksplashFeature = useShowBacksplashFeature({ controller });
	const showCornerTrimFeature = useShowCornerTrimFeature({ controller });

	let island: Island | null = null;
	const navFeature: Record<string, Feature<unknown>> = {};
	if (controller instanceof BpController && controller.navigation?.islandId) {
		island = controller.kitchen.design.islands.findById(controller.navigation.islandId);

		island?.panels.children.forEach((panel) => {
			const labelToIconMap: Record<string, PanelIconComponent> = {
				'front': FrontPanelIcon,
				'back': BackPanelIcon,
				'back left': BackPanelsIcon,
				'back right': BackPanelsIcon,
				'left': SidePanelsIcon,
				'right': SidePanelsIcon
			};

			const icon: PanelIconComponent | undefined = labelToIconMap[panel.label.toLowerCase()];
			if (!icon) {
				if (process.env.NODE_ENV === 'development') {
					console.warn(`Unknown panel label: ${panel.label}`);
				}
				return;
			}
			if (panel.isSidePanel && navFeature.sides) return;
			if (panel.isBackPanel && navFeature.backs) return;
			const key = panel.isSidePanel ? 'sides' : panel.isBackPanel ? 'backs' : panel.id;
			const label = panel.isSidePanel ? 'sides' : panel.isBackPanel ? 'backs' : panel.label;
			navFeature[key] = showPanelFeature({ controller, panelId: panel.id, label, Icon: icon });
		});

		if (island) navFeature.countertop = showPanelFeature({ controller, label: 'countertop', Icon: SquareX });
	}

	return (
		<Box w={'calc(100% - 2px)'} h={'calc(100% - 2px)'} m={'1px'}>
			<Grid templateRows={'5px 1fr'} w={'100%'} h={'100%'} gap={'0'}>
				<GridItem><Divider orientation={'vertical'} /></GridItem>
				<GridItem border="0px">
					<VStack>
						<RtaFeatureIconButton feature={displaySkuOverheadModeFeature} />
						<RtaFeatureIconButton feature={displayOverheadModeFeature} />
						<RtaFeatureIconButton feature={manufacturingModeFeature} />
						<Divider orientation={'horizontal'} />
						<RtaFeatureIconButton feature={blueprintViewFeature} />

						<Divider orientation={'horizontal'} />
						{
							Object.keys(navFeature).map((key) => (
								<RtaFeatureIconButton key={key} feature={navFeature[key]} iconProps={{ fontSize: '.8em' }} />
							))
						}
						<RtaFeatureIconButton feature={showCornerTrimFeature} iconProps={{ fontSize: '.8em' }} />
						<RtaFeatureIconButton feature={showBacksplashFeature} iconProps={{ fontSize: '.8em' }} />
						<Divider h={'2px'} my={'2px'} orientation={'horizontal'}/>

						<RtaFeatureIconButton feature={showAllDimensions} />
						<Divider h={'2px'} my={'2px'} orientation={'horizontal'}/>
						<RtaFeatureIconButton feature={increaseFontSize} />
						<Text fontSize=".5em">{Math.floor((controller?.options?.dimensionFontSizeFactor ?? 1.2) * 100) / 100}</Text>
						<RtaFeatureIconButton feature={decreaseFontSize} />
						<Divider h={'2px'} my={'2px'} orientation={'horizontal'}/>
					</VStack>
				</GridItem>
			</Grid>
		</Box>
	);
};
